import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { usePassengerCondition, useResponsibleAdultLink } from '@/lib/disruptions/use-disruptions';
import { SETTABLE_CONDITIONS, type DisruptionPaxRosterRow, type PassengerCondition } from '@/lib/disruptions/types';

// The desk's two writes behind the minors rule (#806, SKY PoC §4.4): set a
// passenger's adult / minor condition, or link a minor to their responsible adult
// on ANOTHER PNR. Rendered inside the PNR drawer's passenger row. Open by default
// when the condition is UNKNOWN — that passenger holds the whole party back from
// hotel and transport assignment, so the fix must be one click away — and folded
// otherwise, since a correction is the exception.

const INPUT_CLASS =
  'rounded-md border border-input bg-background px-2 py-1 text-sm disabled:opacity-60';

export function PassengerConditionControl({
  row,
  caseUrn,
  disruptionUrn,
}: {
  row: DisruptionPaxRosterRow;
  caseUrn: string;
  disruptionUrn: string;
}) {
  const { t } = useTranslation('pnr');
  const unknown = row.condition === 'UNKNOWN';
  const setCondition = usePassengerCondition(disruptionUrn);
  const link = useResponsibleAdultLink(disruptionUrn);
  const [chosen, setChosen] = useState<PassengerCondition | ''>('');
  const [locator, setLocator] = useState('');
  const [linkedTo, setLinkedTo] = useState<{ pnr: string; resolved: boolean } | null>(null);
  const busy = setCondition.isPending || link.isPending;

  const save = async (): Promise<void> => {
    if (!chosen) return;
    try {
      await setCondition.mutateAsync({ subCaseUrn: row.subCaseUrn, paxUrn: row.paxUrn, caseUrn, condition: chosen });
      setChosen('');
    } catch {
      // isError renders inline; the control stays open to retry.
    }
  };

  const linkAdult = async (): Promise<void> => {
    const pnr = locator.trim().toUpperCase();
    if (!pnr) return;
    try {
      const result = await link.mutateAsync({ subCaseUrn: row.subCaseUrn, paxUrn: row.paxUrn, caseUrn, pnrLocator: pnr });
      setLinkedTo({ pnr: result.responsibleAdult.pnrLocator, resolved: result.resolved });
      setLocator('');
    } catch {
      // isError renders inline.
    }
  };

  return (
    <details
      className="mt-2 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-xs"
      open={unknown}
      data-testid="pax-condition-control"
    >
      <summary className={unknown ? 'cursor-pointer font-medium text-destructive' : 'cursor-pointer text-muted-foreground'}>
        {unknown ? t('condition.control.summaryUnknown') : t('condition.control.summary')}
      </summary>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-muted-foreground">{t('condition.control.selectLabel')}</span>
          <select
            value={chosen}
            onChange={(e) => setChosen(e.target.value as PassengerCondition | '')}
            disabled={busy}
            className={INPUT_CLASS}
            data-testid="pax-condition-select"
          >
            <option value="">{t('condition.control.selectPlaceholder')}</option>
            {SETTABLE_CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {t(`condition.value.${c}`)}
              </option>
            ))}
          </select>
        </label>
        <Button type="button" size="sm" disabled={busy || !chosen} onClick={() => void save()} data-testid="pax-condition-save">
          {setCondition.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
          {t('condition.control.save')}
        </Button>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-muted-foreground">{t('condition.control.linkLabel')}</span>
          <input
            value={locator}
            onChange={(e) => setLocator(e.target.value)}
            placeholder={t('condition.control.linkPlaceholder')}
            maxLength={32}
            disabled={busy}
            className={`${INPUT_CLASS} uppercase`}
            data-testid="pax-responsible-adult-input"
          />
        </label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy || !locator.trim()}
          onClick={() => void linkAdult()}
          data-testid="pax-responsible-adult-link"
        >
          {link.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
          {t('condition.control.link')}
        </Button>
      </div>
      {linkedTo ? (
        <p className="mt-1 text-muted-foreground" data-testid="pax-responsible-adult-linked">
          {linkedTo.resolved
            ? t('condition.control.linked', { pnr: linkedTo.pnr })
            : t('condition.control.linkedUnresolved', { pnr: linkedTo.pnr })}
        </p>
      ) : row.responsibleAdult ? (
        <p className="mt-1 text-muted-foreground">{t('condition.responsibleAdult', { pnr: row.responsibleAdult.pnrLocator })}</p>
      ) : null}
      {setCondition.isError || link.isError ? (
        <p className="mt-1 text-destructive" role="alert" data-testid="pax-condition-error">
          {t('condition.control.error')}
        </p>
      ) : null}
    </details>
  );
}
